import { getUser } from 'API';
import {
  lawyerZoneFromServerUser,
  lawyerZoneFromToken,
  readLawyerAreaFromSession,
  resolveRepresentativeRedirect,
  RepresentativeRedirect,
  tokenIncludesUserRanks,
} from 'API/lawyerAccess';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

interface IUseRepresentativeRouteParams {
  accessToken: string | null;
  canRenderApp: boolean;
  needsContactDetailsOnboarding: boolean;
}

export function useRepresentativeRoute({ accessToken, canRenderApp, needsContactDetailsOnboarding }: IUseRepresentativeRouteParams) {
  const { pathname } = useLocation();
  const [serverLawyerZone, setServerLawyerZone] = useState<boolean | null>(null);

  const ranksKnown = tokenIncludesUserRanks(accessToken);

  useEffect(() => {
    if (!canRenderApp || !accessToken || ranksKnown) return;

    let cancelled = false;
    setServerLawyerZone(null);

    (async () => {
      try {
        const user = await getUser();
        if (cancelled) return;
        setServerLawyerZone(lawyerZoneFromServerUser(Boolean(user?.isRepresentativeAuthenticatedUser), Boolean(user?.isShikumAuthenticatedUser), readLawyerAreaFromSession()));
      } catch {
        if (!cancelled) setServerLawyerZone(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canRenderApp, accessToken, ranksKnown]);

  const inLawyerZone = useMemo(() => {
    if (!canRenderApp) return null;
    if (ranksKnown) {
      return lawyerZoneFromToken(accessToken, readLawyerAreaFromSession());
    }
    return serverLawyerZone;
  }, [canRenderApp, accessToken, ranksKnown, serverLawyerZone]);

  const waitingForDecision = canRenderApp && Boolean(accessToken) && !ranksKnown && serverLawyerZone === null;

  const redirectTo: RepresentativeRedirect = useMemo(() => {
    if (!canRenderApp || waitingForDecision || inLawyerZone === null) {
      return null;
    }
    return resolveRepresentativeRedirect(inLawyerZone, pathname, needsContactDetailsOnboarding);
  }, [canRenderApp, waitingForDecision, inLawyerZone, pathname, needsContactDetailsOnboarding]);

  return { waitingForDecision, redirectTo };
}
